import {
  Box,
  Collapse,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useTheme } from '@emotion/react';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { tokens } from '../../../../theme';
import { Button } from '../../../../components/Button';
import { formatMoney, formatRate } from '../../../../utils';
import { useTranslation } from '../../../../hooks/useTranslation';

const SectionTitle = ({ icon, title, colors }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
    <Box sx={{ color: colors.greenAccent[500] }}>{icon}</Box>
    <Typography sx={{ fontWeight: 700, fontSize: '14px' }}>{title}</Typography>
  </Box>
);

const InfoRow = ({ label, value, green }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75 }}>
    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#555' }}>{label}</Typography>
    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: green ? '#4caf50' : '#222' }}>
      {value ?? '-'}
    </Typography>
  </Box>
);

const selectSx = {
  backgroundColor: '#fff',
  fontSize: '13px',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e0e0e0' },
};

const buildMockInstallments = (simulation) => {
  const count = simulation?.installments ?? 6;
  const value = simulation?.installment_value ?? 25625;
  const baseDate = new Date('2024-02-15');
  return Array.from({ length: count }, (_, i) => {
    const d = new Date(baseDate);
    d.setMonth(d.getMonth() + i);
    return { num: i + 1, dueDate: d.toLocaleDateString('pt-BR'), value };
  });
};

const Step1DadosContrato = ({ simulation, formData, updateFormData, onNext, options }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation();
  const [showParcelas, setShowParcelas] = useState(true);
  const [showParams, setShowParams] = useState(true);

  const installments = buildMockInstallments(simulation);

  const totalValue = simulation?.total_value ?? 150000;
  const liquidValue = simulation?.financed_value ?? 147500;
  const emissionCost = simulation?.operational_fee ?? 1000;
  const monthlyRate = simulation?.interest_rate ?? 2.5;
  const installmentCount = simulation?.installments ?? 6;
  const installmentValue = simulation?.installment_value ?? 25625;

  const s = 'createContractModal.step1';

  return (
    <Box>
      {/* Simulação da Revenda */}
      <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2.5, mb: 3, backgroundColor: '#fafafa' }}>
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
          onClick={() => setShowParcelas((v) => !v)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ color: colors.greenAccent[500] }}>
              <DescriptionOutlinedIcon fontSize="small" />
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: '14px' }}>{t(`${s}.simulationSection`)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: '12px', color: colors.greenAccent[500], fontWeight: 600 }}>
              {showParcelas ? t(`${s}.hideParcelas`) : t(`${s}.showParcelas`)}
            </Typography>
            {showParcelas ? (
              <ExpandLessIcon sx={{ color: colors.greenAccent[500], fontSize: 18 }} />
            ) : (
              <ExpandMoreIcon sx={{ color: colors.greenAccent[500], fontSize: 18 }} />
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 4, mt: 2, flexWrap: 'wrap' }}>
          {[
            { label: t(`${s}.totalValue`), value: formatMoney(totalValue) },
            { label: t(`${s}.installments`), value: `${installmentCount}x` },
            { label: t(`${s}.periodicity`), value: 'Mensal' },
            { label: t(`${s}.firstInstallmentDate`), value: '15/02/2024' },
          ].map((item) => (
            <Box key={item.label}>
              <Typography sx={{ fontSize: '12px', color: '#9e9e9e' }}>{item.label}</Typography>
              <Typography sx={{ fontWeight: 700, fontSize: '14px' }}>{item.value}</Typography>
            </Box>
          ))}
          <Box>
            <Typography sx={{ fontSize: '12px', color: '#9e9e9e' }}>{t(`${s}.rate`)}</Typography>
            <Typography sx={{ fontWeight: 700, fontSize: '14px', color: colors.greenAccent[500] }}>
              {monthlyRate}% {t(`${s}.monthlyRateSuffix`)}
            </Typography>
          </Box>
        </Box>

        <Collapse in={showParcelas}>
          <Box sx={{ mt: 2, overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {[t(`${s}.columns.installment`), t(`${s}.columns.firstDueDate`), t(`${s}.columns.value`)].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 600, fontSize: '12px', color: '#555' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {installments.map((inst) => (
                  <TableRow key={inst.num}>
                    <TableCell sx={{ fontSize: '12px' }}>{inst.num}</TableCell>
                    <TableCell sx={{ fontSize: '12px' }}>{inst.dueDate}</TableCell>
                    <TableCell sx={{ fontSize: '12px', color: colors.greenAccent[500], fontWeight: 600 }}>
                      {formatMoney(inst.value)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Collapse>
      </Box>

      {/* Parâmetros */}
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', mb: 2 }}
          onClick={() => setShowParams((v) => !v)}
        >
          <SectionTitle icon={<TuneIcon fontSize="small" />} title={t(`${s}.paramsSection`)} colors={colors} />
          {showParams ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Box>

        <Collapse in={showParams}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label={t(`${s}.issueDate`)}
                type="date"
                value={formData.dataEmissao}
                onChange={(e) => updateFormData({ dataEmissao: e.target.value, hasSimulated: false })}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiInputBase-root': { fontSize: '13px', backgroundColor: '#fff' } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: '13px' }}>{t(`${s}.bankizer`)}</InputLabel>
                <Select
                  value={formData.bancarizadorStep1}
                  label={t(`${s}.bankizer`)}
                  onChange={(e) => updateFormData({ bancarizadorStep1: e.target.value, hasSimulated: false })}
                  sx={selectSx}
                >
                  {(options?.bancarizadores ?? []).map((b) => (
                    <MenuItem key={b.id} value={b.id} sx={{ fontSize: '13px' }}>
                      {b.name} | {b.cnpj}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: '13px' }}>{t(`${s}.calculationBase`)}</InputLabel>
                <Select
                  value={formData.baseCalculo}
                  label={t(`${s}.calculationBase`)}
                  onChange={(e) => updateFormData({ baseCalculo: e.target.value, hasSimulated: false })}
                  sx={selectSx}
                >
                  {(options?.basesCalculo ?? []).map((b) => (
                    <MenuItem key={b.value} value={b.value} sx={{ fontSize: '13px' }}>{b.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: '13px' }}>{t(`${s}.calculationTable`)}</InputLabel>
                <Select
                  value={formData.tabelaCalculo}
                  label={t(`${s}.calculationTable`)}
                  onChange={(e) => updateFormData({ tabelaCalculo: e.target.value, hasSimulated: false })}
                  sx={selectSx}
                >
                  {(options?.tabelasCalculo ?? []).map((tb) => (
                    <MenuItem key={tb.value} value={tb.value} sx={{ fontSize: '13px' }}>{tb.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: '13px' }}>{t(`${s}.index`)}</InputLabel>
                <Select
                  value={formData.indice}
                  label={t(`${s}.index`)}
                  onChange={(e) => updateFormData({ indice: e.target.value, hasSimulated: false })}
                  sx={selectSx}
                >
                  {(options?.indices ?? []).map((idx) => (
                    <MenuItem key={idx.value} value={idx.value} sx={{ fontSize: '13px' }}>{idx.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label={t(`${s}.principalGrace`)}
                type="number"
                value={formData.carenciaPrincipal}
                onChange={(e) => updateFormData({ carenciaPrincipal: Number(e.target.value), hasSimulated: false })}
                fullWidth
                size="small"
                inputProps={{ min: 0 }}
                sx={{ '& .MuiInputBase-root': { fontSize: '13px', backgroundColor: '#fff' } }}
              />
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button
              variant="contained"
              fullWidth={false}
              onClick={() => updateFormData({ hasSimulated: true })}
              startIcon={<DescriptionOutlinedIcon />}
              sx={{ minWidth: 180 }}
            >
              {t(`${s}.simulateButton`)}
            </Button>
          </Box>
        </Collapse>
      </Box>

      {/* Dados do contrato — 2 blocos separados */}
      <SectionTitle icon={<ReceiptLongOutlinedIcon fontSize="small" />} title={t(`${s}.contractDataSection`)} colors={colors} />
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2.5, height: '100%' }}>
            <InfoRow label={t(`${s}.grossValue`)} value={formatMoney(totalValue)} />
            <InfoRow label={t(`${s}.netValue`)} value={formatMoney(liquidValue)} />
            <InfoRow label={t(`${s}.issuanceCost`)} value={formatMoney(emissionCost)} />
            <InfoRow label={t(`${s}.index`)} value={formData.indice} />
            <InfoRow label={t(`${s}.periodicity`)} value="Mensal" />
            <InfoRow label={t(`${s}.calculationBase`)} value={(options?.basesCalculo ?? []).find((b) => b.value === formData.baseCalculo)?.label ?? '-'} />
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2.5, height: '100%' }}>
            <InfoRow label={t(`${s}.monthlyInterest`)} value={formatRate(monthlyRate)} />
            <InfoRow label={t(`${s}.totalInterest`)} value={formatMoney(38820)} />
            <InfoRow label={t(`${s}.cet`)} value={formatRate(3.12)} />
            <InfoRow label={t(`${s}.iof`)} value={formatMoney(1500)} />
            <InfoRow label={t(`${s}.installments`)} value={String(installmentCount)} />
            <InfoRow label={t(`${s}.installmentValue`)} value={formatMoney(installmentValue)} green />
          </Box>
        </Grid>
      </Grid>

      {/* Tabela detalhada de parcelas */}
      <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2.5, mb: 3 }}>
        <SectionTitle icon={<ReceiptLongOutlinedIcon fontSize="small" />} title={t(`${s}.installmentsSection`)} colors={colors} />
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {[
                  t(`${s}.columns.installment`),
                  t(`${s}.columns.dueDate`),
                  t(`${s}.columns.day`),
                  t(`${s}.columns.amortization`),
                  t(`${s}.columns.interest`),
                  t(`${s}.columns.indexer`),
                  t(`${s}.columns.value`),
                ].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 600, fontSize: '12px', color: '#555' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {installments.map((inst, i) => (
                <TableRow key={inst.num}>
                  <TableCell sx={{ fontSize: '12px' }}>{inst.num}</TableCell>
                  <TableCell sx={{ fontSize: '12px' }}>{inst.dueDate}</TableCell>
                  <TableCell sx={{ fontSize: '12px' }}>{i === 0 ? 1 : i * 30}</TableCell>
                  <TableCell sx={{ fontSize: '12px' }}>{formatMoney(1495 + i * 65)}</TableCell>
                  <TableCell sx={{ fontSize: '12px' }}>{formatMoney(3750 - i * 50)}</TableCell>
                  <TableCell sx={{ fontSize: '12px' }}>R$ 0,00</TableCell>
                  <TableCell sx={{ fontSize: '12px', color: colors.greenAccent[500], fontWeight: 600 }}>
                    {formatMoney(inst.value)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Box>

      {/* Rodapé */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
        <Button
          variant="contained"
          fullWidth={false}
          disabled={!formData.hasSimulated}
          onClick={onNext}
          sx={{ minWidth: 120 }}
        >
          {t(`${s}.nextButton`)}
        </Button>
      </Box>
    </Box>
  );
};

export default Step1DadosContrato;
